import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import MessageDialog from "../../components/MessageDialog";
import StorageService from "../../services/StorageService";
import pettyCashService from "../../services/pettyCashService";
import PettyCashReportService from "../../services/pettyCashReportService";
import utility from "../../utils/utility";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../assets/images/deviops_logo.png";
import { addPdfLogo, getPdfLogoDataUrl } from "../../utils/pdfLogo";

export default function PettyCashExpenseReport() {
  const activeCompany = StorageService.activeCompany();
  const companyId = activeCompany?.companyId || "";

  const [employeeId, setEmployeeId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [employees, setEmployees] = useState([]);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState({
    openingBalance: 0,
    totalReceipt: 0,
    totalPayment: 0,
    closingBalance: 0,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("error");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const formatInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentMonthDateRange = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: formatInputDate(startDate),
      endDate: formatInputDate(endDate),
    };
  };

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const formatProjectDisplay = (project) => {
    if (!project) return "-";

    const projectName = project.projectName || project.name || "";
    const categoryName = project.projectCategoryName || project.categoryName || project.category || "";
    const projectCode = project.projectCode || "";
    const categoryInitial = String(categoryName).trim().charAt(0).toUpperCase();

    if (projectName) {
      if (categoryInitial) {
        return `${categoryInitial}*${projectName}`;
      }

      if (projectCode) {
        const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
        return `${codeInitial}*${projectName}`;
      }

      return projectName;
    }

    if (projectCode) {
      const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
      return `${codeInitial}*`;
    }

    return categoryInitial ? `${categoryInitial}*` : "-";
  };

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    return amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getEmployeeLabel = () => {
    if (!employeeId) return "All";
    const found = employees.find((employee) => String(employee.id) === String(employeeId));
    return found ? `${found.firstName || ""} ${found.lastName || ""}`.trim() : employeeId;
  };

  const loadDropdowns = async () => {
    try {
      const employeeRes = await pettyCashService.getEmployeesByCompany(companyId);

      setEmployees(employeeRes?.data || []);
    } catch (error) {
      console.error(error);
      showMessage("Failed to load filter dropdowns");
    }
  };

  const loadReport = async (pageNumber = page) => {
    if (startDate && !endDate) {
      showMessage("Start Date and End Date are required");
      return;
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      showMessage("Start Date should be less than or equal to End Date");
      return;
    }

    setLoading(true);
    try {
      setReports([]);
      setSummary({
        openingBalance: 0,
        totalReceipt: 0,
        totalPayment: 0,
        closingBalance: 0,
      });
      setPage(1);
      const res = await PettyCashReportService.getReport({
        companyId: companyId || undefined,
        employeeId: employeeId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        page: pageNumber,
        limit,
      });

      setReports(res?.data || []);
      setSummary(res?.summary || {});
      setTotalPages(res?.pagination?.totalPages || 0);
      setTotalRecords(res?.pagination?.total || 0);
      setPage(pageNumber);
    } catch (error) {
      console.error(error);
      setReports([]);
      showMessage("Error fetching petty cash report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      loadDropdowns();
      const { startDate: monthStart, endDate: monthEnd } = getCurrentMonthDateRange();
      setStartDate(monthStart);
      setEndDate(monthEnd);
    }
  }, [companyId]);

  const handleClearFilters = () => {
    setEmployeeId("");
    const { startDate: monthStart, endDate: monthEnd } = getCurrentMonthDateRange();
    setStartDate(monthStart);
    setEndDate(monthEnd);
    setReports([]);
    setSummary({
      openingBalance: 0,
      totalReceipt: 0,
      totalPayment: 0,
      closingBalance: 0,
    });
    setPage(1);
    setTotalPages(0);
    setTotalRecords(0);
    loadReport(1);
  };

  const getAllReportData = async () => {
    try {
      const res = await PettyCashReportService.getReport({
        companyId: companyId || undefined,
        employeeId: employeeId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        page: 1,
        limit: 100000,
      });

      return res;
    } catch (error) {
      console.error("Export error:", error);
      return null;
    }
  };

  const exportExcel = async () => {
    const res = await getAllReportData();
    const allData = res?.data || [];

    if (!allData.length) {
      showMessage("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = {};
    ws["!merges"] = [];
    const thin = { style: "thin" };
    const center = { horizontal: "center", vertical: "center", wrapText: true };
    const border = { top: thin, bottom: thin, left: thin, right: thin };
    let row = 0;

    XLSX.utils.sheet_add_aoa(ws, [[activeCompany?.companyName || "Company"]], { origin: { r: row, c: 0 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = { font: { bold: true, sz: 12 } };
    XLSX.utils.sheet_add_aoa(ws, [[`Report Date: ${utility.formatExportDateTime(new Date())}`]], { origin: { r: row, c: 5 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 5 })].s = { font: { bold: true, sz: 10 }, alignment: { horizontal: "right" } };
    row += 2;

    XLSX.utils.sheet_add_aoa(ws, [["Petty Cash Expense Report"]], { origin: { r: row, c: 0 } });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 5 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = { font: { bold: true, sz: 16 }, alignment: center };
    row += 2;

    XLSX.utils.sheet_add_aoa(ws, [
      ["Employee Name:", getEmployeeLabel(), "", "Start Date:", startDate ? utility.formatDateDDMMMYYYY(startDate) : "-"],
      ["End Date:", endDate ? utility.formatDateDDMMMYYYY(endDate) : "-"],
    ], { origin: { r: row, c: 0 } });

    for (let i = 0; i < 2; i++) {
      for (let c = 0; c < 5; c++) {
        const cell = XLSX.utils.encode_cell({ r: row + i, c });
        if (!ws[cell]) continue;
        ws[cell].s = {
          font: { bold: c === 0 || c === 3, sz: 10 },
          border,
        };
      }
    }
    row += 3;

    const headers = ["Date", "Project", "Particulars", "Receipts", "Payments", "Balance"];
    const headerRowIndex = row;
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: { r: row, c: 0 } });
    headers.forEach((_, colIndex) => {
      ws[XLSX.utils.encode_cell({ r: row, c: colIndex })].s = {
        font: { bold: true, sz: 11 },
        alignment: center,
        border,
      };
    });
    row++;

    XLSX.utils.sheet_add_aoa(ws, [[startDate ? utility.formatDateDDMMMYYYY(startDate) : "-", "", "Opening Balance", "", "", res.summary?.openingBalance || 0]], { origin: { r: row, c: 0 } });
    for (let c = 0; c < 6; c++) {
      const cell = XLSX.utils.encode_cell({ r: row, c });
      if (!ws[cell]) {
        ws[cell] = { t: "s", v: "" };
      }
      ws[cell].s = {
        alignment: { horizontal: c >= 3 ? "right" : "left", vertical: "center" },
        border,
      };
      if (c === 5) ws[cell].z = "#,##0.00";
    }
    row++;

    allData.forEach((item) => {
      const receiptVal = Number(item.receiptAmount || 0);
      const paymentVal = Number(item.paymentAmount || 0);
      const balanceVal = Number(item.runningBalance || 0);
      const dateSerial = utility.convertDateToExcelSerial(item.pettycashDate);

      const rowData = [
        dateSerial || "-",
        formatProjectDisplay(item),
        item.Details || "-",
        receiptVal,
        paymentVal,
        balanceVal,
      ];
      XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });
      rowData.forEach((_, colIndex) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
        ws[cell].s = { alignment: { horizontal: colIndex >= 3 ? "right" : (colIndex === 0 ? "center" : "left"), vertical: "center" }, border };
        if (colIndex === 0 && dateSerial) {
          ws[cell].t = "n";
          ws[cell].v = dateSerial;
          ws[cell].z = "dd-mm-yyyy";
        }
        if (colIndex >= 3) {
          ws[cell].t = "n";
          ws[cell].v = colIndex === 3 ? receiptVal : colIndex === 4 ? paymentVal : balanceVal;
          ws[cell].z = "#,##0.00";
        }
      });
      row++;
    });

    ws["!autofilter"] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowIndex, c: 0 },
        e: { r: row - 1, c: headers.length - 1 }
      })
    };

    const totRec = Number(res.summary?.totalReceipt || 0);
    const totPay = Number(res.summary?.totalPayment || 0);
    const totBal = Number(res.summary?.closingBalance || 0);
    XLSX.utils.sheet_add_aoa(ws, [["", "", "Totals:", totRec, totPay, totBal]], { origin: { r: row, c: 0 } });
    for (let c = 0; c < 6; c++) {
      const cell = XLSX.utils.encode_cell({ r: row, c });
      if (!ws[cell]) continue;
      ws[cell].s = {
        font: { bold: true },
        border,
        alignment: { horizontal: c === 2 || c >= 3 ? "right" : "left", vertical: "center" },
      };
      if (c >= 3) {
        ws[cell].t = "n";
        ws[cell].v = c === 3 ? totRec : c === 4 ? totPay : totBal;
        ws[cell].z = "#,##0.00";
      }
    }
    row += 2;

    const totalsStartRow = row;
    XLSX.utils.sheet_add_aoa(ws, [["Total Receipts", "Total Payments", "Balance"], [totRec, totPay, totBal]], { origin: { r: row, c: 0 } });
    for (let r = totalsStartRow; r <= totalsStartRow + 1; r++) {
      for (let c = 0; c < 3; c++) {
        const cell = XLSX.utils.encode_cell({ r, c });
        if (!ws[cell]) continue;
        ws[cell].s = {
          font: { bold: r === totalsStartRow },
          alignment: { horizontal: r === totalsStartRow ? "center" : "right", vertical: "center" },
          border,
        };
        if (r === totalsStartRow + 1) {
          ws[cell].t = "n";
          ws[cell].v = c === 0 ? totRec : c === 1 ? totPay : totBal;
          ws[cell].z = "#,##0.00";
        }
      }
    }
    row += 3;

    const projectSummary = (res.projectSummary || []).filter(
      (item) => item && (item.projectName || item.projectCode || Number(item.paymentAmount))
    );

    if (projectSummary.length > 0) {
      const projectSummaryStartRow = row;
      XLSX.utils.sheet_add_aoa(ws, [["Sr.No", "Project Name", "Payments"]], { origin: { r: row, c: 0 } });
      for (let c = 0; c < 3; c++) {
        const cell = XLSX.utils.encode_cell({ r: row, c });
        ws[cell].s = { font: { bold: true, sz: 11 }, alignment: center, border };
      }
      row++;

      projectSummary.forEach((item, index) => {
        const pAmt = Number(item.paymentAmount || 0);
        XLSX.utils.sheet_add_aoa(
          ws,
          [[index + 1, formatProjectDisplay(item), pAmt]],
          { origin: { r: row, c: 0 } }
        );
        for (let c = 0; c < 3; c++) {
          const cell = XLSX.utils.encode_cell({ r: row, c });
          if (!ws[cell]) ws[cell] = { t: "s", v: "" };
          ws[cell].s = {
            alignment: { horizontal: c === 0 ? "center" : c === 2 ? "right" : "left", vertical: "center" },
            border,
          };
          if (c === 0) {
            ws[cell].t = "n";
            ws[cell].v = index + 1;
            ws[cell].z = "0";
          }
          if (c === 2) {
            ws[cell].t = "n";
            ws[cell].v = pAmt;
            ws[cell].z = "#,##0.00";
          }
        }
        row++;
      });

      XLSX.utils.sheet_add_aoa(ws, [["", "Total:", totPay]], { origin: { r: row, c: 0 } });
      for (let c = 0; c < 3; c++) {
        const cell = XLSX.utils.encode_cell({ r: row, c });
        if (!ws[cell]) ws[cell] = { t: "s", v: "" };
        ws[cell].s = {
          font: { bold: true },
          alignment: { horizontal: c === 0 ? "center" : c >= 1 ? "right" : "left", vertical: "center" },
          border,
        };
        if (c === 2) {
          ws[cell].t = "n";
          ws[cell].v = totPay;
          ws[cell].z = "#,##0.00";
        }
      }
    }

    ws["!cols"] = [{ wch: 14 }, { wch: 24 }, { wch: 36 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws, "PettyCashReport");
    XLSX.writeFile(wb, "PettyCashExpenseReport.xlsx");
  };

  const exportPdf = async () => {
    const res = await getAllReportData();
    const allData = res?.data || [];

    if (!allData.length) {
      showMessage("No data to export");
      return;
    }

    const doc = new jsPDF("portrait");
    const pdfLogo = await getPdfLogoDataUrl(logo);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageMargin = 14;
    const right = pageWidth - pageMargin;
    const left = pageMargin;
    const companyName = activeCompany?.companyName || "Company";
    const title = "Petty Cash Expense Report";

    const drawHeader = (pageNumber) => {
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.2);

      doc.setFont(undefined, "bold");
      doc.setFontSize(12);
      const companyNameWidth = doc.getTextWidth(companyName);
      const logoWidth = 5;
      const logoHeight = 10;
      const logoX = left + 6 + (companyNameWidth - logoWidth) / 2;
      const logoY = 8;

      doc.addImage(pdfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);
      doc.text(companyName, left + 6, 23);

      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(`Report Date: ${utility.formatExportDateTime(new Date())}`, right, 23, { align: "right" });

      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text(title, pageWidth / 2, 35, { align: "center" });

      const titleWidth = doc.getTextWidth(title);
      doc.setLineWidth(0.3);
      doc.line(pageWidth / 2 - titleWidth / 2, 37, pageWidth / 2 + titleWidth / 2, 37);
    };

    drawHeader(1);
    let yPosition = 42;
    doc.setFontSize(9);
    doc.text("Employee Name:", 14, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(getEmployeeLabel(), 43, yPosition);
    doc.setFont(undefined, "bold");
    doc.text("Start Date:", 14, yPosition + 6);
    doc.setFont(undefined, "normal");
    doc.text(startDate ? utility.formatDateDDMMMYYYY(startDate) : "-", 43, yPosition + 6);
    doc.setFont(undefined, "bold");
    doc.text("End Date:", 14, yPosition + 12);
    doc.setFont(undefined, "normal");
    doc.text(endDate ? utility.formatDateDDMMMYYYY(endDate) : "-", 43, yPosition + 12);

    const body = [
      [startDate ? utility.formatDateDDMMMYYYY(startDate) : "-", "", "Opening Balance", "", "", formatAmount(res.summary?.openingBalance)],
      ...allData.map((row) => [
        utility.formatDateDDMMMYYYY(row.pettycashDate),
        formatProjectDisplay(row),
        row.Details || "-",
        row.receiptAmount ? formatAmount(row.receiptAmount) : "",
        row.paymentAmount ? formatAmount(row.paymentAmount) : "",
        formatAmount(row.runningBalance),
      ]),
    ];

    // autoTable(doc, {
    //   startY: yPosition + 20,
    //   head: [["Date", "Project", "Particulars", "Receipts", "Payments", "Balance"]],
    //   body,
    //   foot: [["", "", "Totals:", formatAmount(res.summary?.totalReceipt), formatAmount(res.summary?.totalPayment), formatAmount(res.summary?.closingBalance)]],
    //   showFoot: "lastPage",
    //   theme: "grid",
    //   styles: { fontSize: 8, lineColor: [0, 0, 0], lineWidth: 0.15, textColor: [0, 0, 0] },
    //   headStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: "bold", halign: "center" },
    //   footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold" },
    //   columnStyles: { 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } },
    //   didParseCell: (data) => {
    //     if (data.section === "foot" && [2, 3, 4, 5].includes(data.column.index)) {
    //       data.cell.styles.halign = "right";
    //     }
    //   },
    // });

    autoTable(doc, {
      startY: yPosition + 20,

      head: [[
        "Date",
        "Project",
        "Particulars",
        "Receipts",
        "Payments",
        "Balance"
      ]],

      body,

      foot: [[
        "",
        "",
        "Totals:",
        formatAmount(res.summary?.totalReceipt),
        formatAmount(res.summary?.totalPayment),
        formatAmount(res.summary?.closingBalance)
      ]],

      showFoot: "lastPage",

      theme: "grid",

      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.15,
        textColor: [0, 0, 0]
      },

      headStyles: {
        fillColor: [230, 230, 230], // Light Gray
        textColor: [0, 0, 0], // Dark text
        fontStyle: "bold",
        halign: "center"
      },

      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold"
      },

      columnStyles: {
        0: { halign: "center" },
        1: { halign: "left" },
        2: { halign: "left" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" }
      },

      margin: {
        top: 40,
        left: 14,
        right: 14,
        bottom: 25
      },

      didParseCell: (data) => {

        if (
          data.section === "foot" &&
          [2, 3, 4, 5].includes(data.column.index)
        ) {
          data.cell.styles.halign = "right";
        }
      },

      didDrawPage: function (data) {
        if (data.pageNumber === 1) return;
        drawHeader(data.pageNumber);
      }

    });

    let finalY = doc.lastAutoTable.finalY + 8;
    autoTable(doc, {
      startY: finalY,
      margin: { top: 40, left: 14, right: 14, bottom: 25 },
      head: [["Total Receipts", "Total Payments", "Balance"]],
      body: [[formatAmount(res.summary?.totalReceipt), formatAmount(res.summary?.totalPayment), formatAmount(res.summary?.closingBalance)]],
      theme: "grid",
      styles: { fontSize: 8, lineColor: [0, 0, 0], lineWidth: 0.15, halign: "right" },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
    });

    const projectSummary = (res.projectSummary || []).filter(
      (item) => item && (item.projectName || item.projectCode || Number(item.paymentAmount))
    );

    if (projectSummary.length > 0) {
      finalY = doc.lastAutoTable.finalY + 8;
      autoTable(doc, {
        startY: finalY,
        margin: { top: 40, left: 14, right: 14, bottom: 25 },
        head: [["Sr.No", "Project Name", "Payments"]],
        body: projectSummary.map((item, index) => [
          index + 1,
          formatProjectDisplay(item),
          formatAmount(item.paymentAmount),
        ]),
        foot: [["", "Total:", formatAmount(res.summary?.totalPayment)]],
        showFoot: "lastPage",
        theme: "grid",
        styles: { fontSize: 8, lineColor: [0, 0, 0], lineWidth: 0.15, textColor: [0, 0, 0] },
        headStyles: {
          fillColor: [230, 230, 230],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          halign: "center"
        },
        footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold" },
        columnStyles: { 0: { halign: "center" }, 2: { halign: "right" } },
        didParseCell: (data) => {
          if (data.section === "foot" && [1, 2].includes(data.column.index)) {
            data.cell.styles.halign = "right";
          }
        },
      });
    }

    const pdfPageCount = doc.getNumberOfPages();

    for (let i = 1; i <= pdfPageCount; i++) {
      doc.setPage(i);
      if (i === pdfPageCount) {
        doc.setFont(undefined, "bold");
        doc.setFontSize(8);
        const footerTop = pageHeight - 20;
        doc.text("Prepared By", 25, footerTop);
        doc.text("Checked By", pageWidth / 2, footerTop, { align: "center" });
        doc.text("Approved By", pageWidth - 25, footerTop, { align: "right" });
      }
      doc.setFont(undefined, "normal");
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pdfPageCount}`, pageWidth / 2, pageHeight - 7, { align: "center" });
    }

    doc.save("PettyCashExpenseReport.pdf");
  };

  return (
    <>
      <Breadcrumb pageTitle="Petty Cash Expense Report" secondTitle="Reports" />

      <MessageDialog message={message} type={msgType} setMessage={setMessage} setMsgType={setMsgType} />

      <div className="card p-4 bg-white shadow-sm">
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <label className="form-label">Employee Name</label>
            <select className="form-select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">Select Employee</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {`${employee.firstName || ""} ${employee.lastName || ""}`.trim()}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Start Date</label>
            <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>

          <div className="col-md-3">
            <label className="form-label">End Date</label>
            <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>

        <div className="mb-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              <i className="ti ti-refresh me-1"></i>
              Clear
            </button>
            <button className="btn btn-primary" onClick={() => loadReport(1)} disabled={loading}>
              <i className="ti ti-search me-1"></i>
              {loading ? "Searching..." : "View Report"}
            </button>
          </div>
        </div>
        <hr />
        <div className="mb-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2">
            <button className="btn btn-success" onClick={exportPdf} disabled={loading}>
              <i className="ti ti-file-type-pdf me-1"></i>
              Export PDF
            </button>
            <button className="btn btn-success" onClick={exportExcel} disabled={loading}>
              <i className="ti ti-file-spreadsheet me-1"></i>
              Export Excel
            </button>
          </div>
         
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-striped mb-0">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Project</th>
                <th>Particulars</th>
                <th className="text-end">Receipts</th>
                <th className="text-end">Payments</th>
                <th className="text-end">Balance</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center"><div className="spinner-border text-primary" /></td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center">No data found</td>
                </tr>
              ) : (
                <>
                  {page === 1 && (
                    <tr>
                      <td>{startDate ? utility.formatDateDDMMMYYYY(startDate) : "-"}</td>
                      <td></td>
                      <td>Opening Balance</td>
                      <td></td>
                      <td></td>
                      <td className="text-end">{formatAmount(summary.openingBalance)}</td>
                    </tr>
                  )}
                  {reports.map((row) => (
                    <tr key={row.id}>
                      <td>{utility.formatDateDDMMMYYYY(row.pettycashDate)}</td>
                      <td>{formatProjectDisplay(row)}</td>
                      <td>{row.Details || "-"}</td>
                      <td className="text-end">{row.receiptAmount ? formatAmount(row.receiptAmount) : ""}</td>
                      <td className="text-end">{row.paymentAmount ? formatAmount(row.paymentAmount) : ""}</td>
                      <td className="text-end">{formatAmount(row.runningBalance)}</td>
                    </tr>
                  ))}
                  {page === totalPages && (
                    <tr className="fw-bold">
                      <td></td>
                      <td></td>
                      <td className="text-end">Totals:</td>
                      <td className="text-end">{formatAmount(summary.totalReceipt)}</td>
                      <td className="text-end">{formatAmount(summary.totalPayment)}</td>
                      <td className="text-end">{formatAmount(summary.closingBalance)}</td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>Total Records: <strong>{totalRecords}</strong></div>
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-secondary" disabled={page === 1} onClick={() => loadReport(page - 1)}>Prev</button>
                {[...Array(totalPages)]
                  .map((_, i) => i + 1)
                  .filter((p) => p >= page - 2 && p <= page + 2)
                  .map((p) => (
                    <button key={p} className={`btn btn-sm ${page === p ? "btn-primary" : "btn-outline-primary"}`} onClick={() => loadReport(p)}>
                      {p}
                    </button>
                  ))}
                <button className="btn btn-sm btn-secondary" disabled={page === totalPages} onClick={() => loadReport(page + 1)}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
